import BigNumber from 'bignumber.js'
import { DEFAULT_TOKEN_DECIMAL, PINATA_BASE_URL } from 'config'
import ERC20ABI from 'config/abi/erc20.json'
import NftABI from 'config/abi/nft.json'
import NftPoolABI from 'config/abi/nftPool.json'
import NftPoolWithMcABI from 'config/abi/nftPoolWithMC.json'
import { SeriesKey } from 'config/constants/types'
import { Series } from 'state/types'
import { getAddress } from 'utils/addressHelpers'
import multicall, { multicallWithAbi } from 'utils/multicall'
import { getJsonImageFromUri } from 'utils/getCollectionData'
import { BIG_ZERO } from 'utils/bigNumber'
import { fetchMinteds } from './fetchPublicSeriesData'

const fetchUserBalances = async (serieses: Series[], account: string) => {
  const seriesesToFetch = serieses.filter((series) => !series.notFromERC721Enumerable)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.address),
    name: 'balanceOf',
    params: [account],
  }))
  const balancesOfFC = await multicall(NftABI, calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: new BigNumber(balancesOfFC[index]).toNumber(),
    }),
    {},
  )
}

const fetchStakedUserInfos = async (serieses: Series[], account: string) => {
  const seriesesToFetch = serieses.filter((series) => series.pool)
  const calls = seriesesToFetch.map((series) => ({
    address: getAddress(series.pool),
    name: 'getUserInfo',
    params: series.withoutMC ? [account] : [getAddress(series.address), account],
    abi: series.withoutMC ? NftPoolABI : NftPoolWithMcABI,
  }))
  const stakedUserInfosFC = await multicallWithAbi(calls)

  return seriesesToFetch.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: stakedUserInfosFC[index],
    }),
    {},
  )
}

const fetchUserNftIds = async (account: string, series: Series, userBalance: number, stakedUserInfo: any) => {
  const tokenIdsOfUser = []
  if (series.notFromERC721Enumerable) {
    const calls0 = [...Array(series.key === SeriesKey.SLOTH_AVATAR ? series.minted - 1 : series.minted)].map(
      (item, index) => {
        return {
          address: getAddress(series.address),
          name: 'ownerOf',
          params: [series.key === SeriesKey.CYBERSTONED ? index : index + 1],
        }
      },
    )
    const ownersFC = await multicall(NftABI, calls0)
    ownersFC
      ?.map((ownerFC, index) => {
        return {
          tokenId: series.key === SeriesKey.CYBERSTONED ? index : index + 1,
          address: ownerFC[0],
        }
      })
      .filter((item) => item.address.toLowerCase() === account.toLowerCase())
      .forEach((item) => {
        tokenIdsOfUser.push({
          nid: item.tokenId,
          isStaked: false,
        })
      })
  } else {
    const calls0 = [...Array(userBalance)].map((item, index) => {
      return {
        address: getAddress(series.address),
        name: 'tokenOfOwnerByIndex',
        params: [account, index],
      }
    })
    const tokenIdsFC = await multicall(NftABI, calls0)
    tokenIdsFC?.forEach((tokenIdFC) => {
      tokenIdsOfUser.push({
        nid: new BigNumber(tokenIdFC).toNumber(),
        isStaked: false,
      })
    })
  }

  if (stakedUserInfo) {
    stakedUserInfo[2].forEach((item) => {
      tokenIdsOfUser.push({
        nid: new BigNumber(item._hex).toNumber(),
        isStaked: true,
      })
    })
  }

  return tokenIdsOfUser
}

const fetchUserNfts = async (account: string, series: Series, userBalance: number, stakedUserInfo: any) => {
  let userNfts = [...(series.userData?.nfts || [])]

  const tokenIdsOfUser = await fetchUserNftIds(account, series, userBalance, stakedUserInfo)
  userNfts = userNfts
    .filter((nft) => tokenIdsOfUser.find((tokenIdOfUser) => tokenIdOfUser.nid === nft.nid))
    .map((nft) => ({
      ...nft,
      isStaked: tokenIdsOfUser.find((tokenIdOfUser) => tokenIdOfUser.nid === nft.nid)?.isStaked,
    }))
  const tokenIdsOfUserToFetch = tokenIdsOfUser.filter(
    (tokenIdOfUser) => !userNfts.find((nft) => nft.nid === tokenIdOfUser.nid),
  )

  if (tokenIdsOfUserToFetch.length) {
    const calls = tokenIdsOfUserToFetch.map((tokenIdOfUser) => ({
      address: getAddress(series.address),
      name: 'tokenURI',
      params: [tokenIdOfUser.nid],
    }))
    const tokenURIsFC = await multicall(NftABI, calls)

    await Promise.all(
      tokenIdsOfUserToFetch.map(async (tokenIdOfUser, i) => {
        try {
          const uri = `${tokenURIsFC[i][0]}`
          if (series.key === SeriesKey.SLURPIESBYZAQ) {
            userNfts.push({
              ...tokenIdOfUser,
            })
          } else {
            const [jsonData, image] = await getJsonImageFromUri(uri, series.key)

            userNfts.push({
              ...tokenIdOfUser,
              ...jsonData,
              image,
              animationUrl: jsonData.animation_url,
            })
          }
        } catch (error) {
          console.error(error)
          userNfts.push(tokenIdOfUser)
        }
      }),
    )
  }

  userNfts.sort((a, b) => {
    return a.nid < b.nid ? 1 : -1
  })

  return userNfts
}

const fetchUserEarned = async (account: string, serieses: Series[]) => {
  const seriesesToFetch = serieses.filter((series) => series.pool)
  const calls = seriesesToFetch.map((series) => {
    if (!series.withoutMC) {
      return {
        address: getAddress(series.pool),
        name: 'pendingToken',
        params: [getAddress(series.address), account],
        abi: NftPoolWithMcABI,
      }
    }
    return {
      address: getAddress(series.pool),
      name: 'pendingDMD',
      params: [account],
      abi: NftPoolABI,
    }
  })

  const userEarnedFC = await multicallWithAbi(calls)

  if (userEarnedFC) {
    return seriesesToFetch.reduce(
      (acc, series, index) => ({
        ...acc,
        [series.sid]: new BigNumber(userEarnedFC[index]).div(DEFAULT_TOKEN_DECIMAL).toNumber(),
      }),
      {},
    )
  } else {
    return {}
  }
}

const fetchUserApproved = async (account: string, serieses: Series[]) => {
  const seriesesHasPool = serieses.filter((series) => series.pool)
  const calls = seriesesHasPool.map((series) => {
    const nftAddress = getAddress(series.address)
    const poolAddress = getAddress(series.pool)
    return {
      address: nftAddress,
      name: 'isApprovedForAll',
      params: [account, poolAddress],
    }
  })
  const approvedFC = await multicall(NftABI, calls)

  return seriesesHasPool.reduce(
    (acc, series, index) => ({
      ...acc,
      [series.sid]: approvedFC[index][0],
    }),
    {},
  )
}

const fetchUserWhitelisted = async (account: string, serieses: Series[]) => {
  const seriesesLaunchPad = serieses.filter((series) => series.isLaunchpad)
  const calls = seriesesLaunchPad.map((series) => {
    const nftAddress = getAddress(series.address)
    return {
      address: nftAddress,
      name: 'isWhitelisted',
      params: [account],
    }
  })
  const isWhitelistedFC = await multicall(NftABI, calls)

  if (isWhitelistedFC) {
    return seriesesLaunchPad.reduce(
      (acc, series, index) => ({
        ...acc,
        [series.sid]: isWhitelistedFC[index][0],
      }),
      {},
    )
  } else {
    return {}
  }
}

const fetchUserTokenAllowances = async (account: string, serieses: Series[]) => {
  const seriesesWithERC20 = serieses.filter((series) => series.token)
  const calls = seriesesWithERC20.map((series) => ({
    address: getAddress(series.token.address),
    name: 'allowance',
    params: [account, getAddress(series.address)],
  }))
  const tokenAllowances = await multicall(ERC20ABI, calls)

  return tokenAllowances
    ? seriesesWithERC20.reduce(
        (acc, series, index) => ({
          ...acc,
          [series.sid]: new BigNumber(tokenAllowances[index]),
        }),
        {},
      )
    : {}
}

const fetchSeriesesUserData = async (serieses: Series[], account: string) => {
  // let userNfts = []
  // let userApproved = {}
  // let userEarned = {}
  // let userWhitelisted = {}
  // let userTokenAllowance = {}

  // const setUserNfts = async () => {
  //   let seriesesWithMinted = []
  //   let userBalances = {}
  //   let stakedUserInfos = {}

  //   const setUserBalances = async () => {
  //     userBalances = await fetchUserBalances(serieses, account)
  //   }
  //   const setSeriesesMinted = async () => {
  //     if (serieses[0].minted) {
  //       seriesesWithMinted = serieses
  //     } else {
  //       const minteds = await fetchMinteds(serieses)
  //       seriesesWithMinted = serieses.map((series) => ({
  //         ...series,
  //         minted: series.preTotalSupply ? series.preTotalSupply : minteds[series.sid] ? minteds[series.sid] : 0,
  //       }))
  //     }
  //   }
  //   const setStakedUserInfos = async () => {
  //     stakedUserInfos = await fetchStakedUserInfos(serieses, account)
  //   }
  //   await Promise.all([setUserBalances(), setSeriesesMinted(), setStakedUserInfos()])

  //   userNfts = await Promise.all(
  //     seriesesWithMinted.map(async (series) => {
  //       const userNft = await fetchUserNfts(
  //         account,
  //         series,
  //         userBalances[series.sid] ? userBalances[series.sid] : 0,
  //         stakedUserInfos[series.sid] ? stakedUserInfos[series.sid] : null,
  //       )
  //       // userStaked = await fetchUserNftIds(account, currentSerieses[i])
  //       return userNft
  //     }),
  //   )
  // }
  // const setUserApproved = async () => {
  //   userApproved = await fetchUserApproved(account, serieses)
  // }
  // const setUserEarned = async () => {
  //   userEarned = await fetchUserEarned(account, serieses)
  // }
  // const setUserWhitelisted = async () => {
  //   userWhitelisted = await fetchUserWhitelisted(account, serieses)
  // }
  // const setUserTokenAllowance = async () => {
  //   userTokenAllowance = await fetchUserTokenAllowances(account, serieses)
  // }
  // await Promise.all([setUserNfts(), setUserApproved(), setUserEarned(), setUserWhitelisted(), setUserTokenAllowance()])
  // // const balances = await fetchUserBalances(account)

  // return serieses.map((series, index) => ({
  //   sid: series.sid,
  //   // balance: balances[series.sid],
  //   nfts: userNfts[index] || [],
  //   approved: userApproved[series.sid] || false,
  //   earned: userEarned[series.sid] || userEarned[series.sid] === 0 ? userEarned[series.sid] : series.userData?.earned,
  //   isWhitelisted:
  //     userWhitelisted[series.sid] || userWhitelisted[series.sid] === false
  //       ? userWhitelisted[series.sid]
  //       : series.userData?.isWhitelisted,
  //   tokenAllowance: userTokenAllowance[series.sid] || BIG_ZERO,
  // }))

  try {
    console.log(serieses)
    const response = await fetch(`${process.env.REACT_APP_API}/nft/${account}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(serieses),
    })
    const result = await response.json()
    if (result.status === 'success') {
      console.log(result.data)
      const seriesesR = await Promise.all(
        result.data.map(async (series) => {
          const nfts = await Promise.all(
            series.nfts.map(async (nft) => {
              let image = nft.image

              image = image?.replace('https://gateway.pinata.cloud/ipfs', PINATA_BASE_URL)
              // image = image?.replace(DMD_PINATA_BASE_URL, PINATA_BASE_URL)
              if (image?.startsWith('ipfs')) {
                image = `${PINATA_BASE_URL}/${image.split('://')[1]}`
              }
              if (image?.startsWith('http')) {
                const imageData = await fetch(image)
                const imageBlob = await imageData.blob()
                image = URL.createObjectURL(imageBlob)
              }
              return {
                ...nft,
                image,
              }
            }),
          )
          return {
            ...series,
            nfts,
          }
        }),
      )
      return seriesesR
    }
    return []
  } catch (error) {
    console.error(error)
    return []
  }
}

export default fetchSeriesesUserData
